import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import {
  getBranches, getDailySales, getBranchPerformance, getLowStock, getCashClose, exportLowStockExcel,
  getSalesByPeriod, getTopProducts, getProfitMargin, getMarketplaceOrdersReport, getQuotesFunnel, getInventoryValuation,
  getPOSTerminals
} from '../../services/api';
import {
  BarChart3, TrendingUp, AlertTriangle, DollarSign, Package, Calendar, Building2, Download,
  ShoppingBag, FileText, Percent, Layers, Wallet
} from 'lucide-react';
import { toast } from 'sonner';

const Reportes = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/top-productos')) return 'top-products';
    if (path.includes('/marketplace')) return 'marketplace';
    if (path.includes('/cotizaciones')) return 'quotes';
    if (path.includes('/inventario')) return 'stock';
    if (path.includes('/ventas')) return 'daily';
    if (path.includes('/caja')) return 'cash';
    return 'daily';
  };

  const [branches, setBranches] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(getTabFromPath);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Date range for period-based reports
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  // Existing report data
  const [dailySales, setDailySales] = useState(null);
  const [branchPerformance, setBranchPerformance] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [cashClose, setCashClose] = useState(null);

  // New report data
  const [salesByPeriod, setSalesByPeriod] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [profitMargin, setProfitMargin] = useState(null);
  const [marketplaceOrders, setMarketplaceOrders] = useState(null);
  const [quotesFunnel, setQuotesFunnel] = useState(null);
  const [inventoryValuation, setInventoryValuation] = useState(null);

  const pathMap = {
    daily: '/dashboard/reportes/ventas',
    period: '/dashboard/reportes/ventas',
    performance: '/dashboard/reportes/ventas',
    cash: '/dashboard/reportes/caja',
    stock: '/dashboard/reportes/inventario',
    'top-products': '/dashboard/reportes/top-productos',
    'profit-margin': '/dashboard/reportes/inventario',
    marketplace: '/dashboard/reportes/marketplace',
    quotes: '/dashboard/reportes/cotizaciones',
    valuation: '/dashboard/reportes/inventario',
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const target = pathMap[tabId];
    if (target && target !== location.pathname) {
      navigate(target, { replace: true });
    }
  };

  useEffect(() => {
    if (user?.business_id) {
      loadInitialData();
    } else {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (selectedBranch) {
      getPOSTerminals(selectedBranch).then(r => setTerminals(r.data || [])).catch(() => setTerminals([]));
    } else {
      setTerminals([]);
    }
    setSelectedTerminal('');
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch && activeTab === 'daily') loadDailySales();
    if (selectedBranch && activeTab === 'cash') loadCashClose();
  }, [selectedBranch, selectedDate, selectedTerminal, activeTab]);

  useEffect(() => {
    if (!user?.business_id) return;
    if (activeTab === 'performance') loadBranchPerformance();
    if (activeTab === 'stock') loadLowStock();
    if (activeTab === 'period') loadSalesByPeriod();
    if (activeTab === 'top-products') loadTopProductsData();
    if (activeTab === 'profit-margin') loadProfitMargin();
    if (activeTab === 'marketplace') loadMarketplaceOrders();
    if (activeTab === 'quotes') loadQuotesFunnel();
    if (activeTab === 'valuation') loadInventoryValuation();
  }, [activeTab, user?.business_id]);

  const loadInitialData = async () => {
    try {
      const branchesRes = await getBranches(user.business_id);
      setBranches(branchesRes.data);
      if (branchesRes.data.length > 0) {
        setSelectedBranch(branchesRes.data[0].id);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDailySales = async () => {
    try {
      const params = { branch_id: selectedBranch, date: selectedDate };
      if (selectedTerminal) params.terminal_id = selectedTerminal;
      const response = await getDailySales(params);
      setDailySales(response.data);
    } catch (error) {
      console.error('Error loading daily sales:', error);
      setDailySales(null);
    }
  };

  const loadBranchPerformance = async () => {
    try {
      const response = await getBranchPerformance({ business_id: user.business_id });
      setBranchPerformance(response.data);
    } catch (error) {
      console.error('Error loading branch performance:', error);
      setBranchPerformance([]);
    }
  };

  const loadLowStock = async () => {
    try {
      const response = await getLowStock(user.business_id);
      setLowStock(response.data);
    } catch (error) {
      console.error('Error loading low stock:', error);
      setLowStock([]);
    }
  };

  const loadCashClose = async () => {
    try {
      const params = { branch_id: selectedBranch, date: selectedDate };
      if (selectedTerminal) params.terminal_id = selectedTerminal;
      const response = await getCashClose(params);
      setCashClose(response.data);
    } catch (error) {
      console.error('Error loading cash close:', error);
      setCashClose(null);
    }
  };

  const loadSalesByPeriod = async () => {
    try {
      const params = { business_id: user.business_id, start_date: startDate, end_date: endDate };
      if (selectedBranch) params.branch_id = selectedBranch;
      if (selectedTerminal) params.terminal_id = selectedTerminal;
      const response = await getSalesByPeriod(params);
      setSalesByPeriod(response.data);
    } catch (error) {
      console.error('Error loading sales by period:', error);
      setSalesByPeriod(null);
    }
  };

  const loadTopProductsData = async () => {
    try {
      const params = { business_id: user.business_id, start_date: startDate, end_date: endDate };
      if (selectedBranch) params.branch_id = selectedBranch;
      if (selectedTerminal) params.terminal_id = selectedTerminal;
      const response = await getTopProducts(params);
      setTopProducts(response.data);
    } catch (error) {
      console.error('Error loading top products:', error);
      setTopProducts([]);
    }
  };

  const loadProfitMargin = async () => {
    try {
      const params = { business_id: user.business_id, start_date: startDate, end_date: endDate };
      if (selectedBranch) params.branch_id = selectedBranch;
      const response = await getProfitMargin(params);
      setProfitMargin(response.data);
    } catch (error) {
      console.error('Error loading profit margin:', error);
      setProfitMargin(null);
    }
  };

  const loadMarketplaceOrders = async () => {
    try {
      const params = { business_id: user.business_id, start_date: startDate, end_date: endDate };
      const response = await getMarketplaceOrdersReport(params);
      setMarketplaceOrders(response.data);
    } catch (error) {
      console.error('Error loading marketplace orders:', error);
      setMarketplaceOrders(null);
    }
  };

  const loadQuotesFunnel = async () => {
    try {
      const params = { business_id: user.business_id, start_date: startDate, end_date: endDate };
      const response = await getQuotesFunnel(params);
      setQuotesFunnel(response.data);
    } catch (error) {
      console.error('Error loading quotes funnel:', error);
      setQuotesFunnel(null);
    }
  };

  const loadInventoryValuation = async () => {
    try {
      const response = await getInventoryValuation({ business_id: user.business_id });
      setInventoryValuation(response.data);
    } catch (error) {
      console.error('Error loading inventory valuation:', error);
      setInventoryValuation(null);
    }
  };

  const handleApplyRangeFilter = () => {
    if (activeTab === 'period') loadSalesByPeriod();
    if (activeTab === 'top-products') loadTopProductsData();
    if (activeTab === 'profit-margin') loadProfitMargin();
    if (activeTab === 'marketplace') loadMarketplaceOrders();
    if (activeTab === 'quotes') loadQuotesFunnel();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await exportLowStockExcel(user.business_id);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_bajo_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar reporte');
    } finally {
      setExportingExcel(false);
    }
  };

  const tabs = [
    { id: 'daily', label: 'Ventas del Dia', icon: Calendar },
    { id: 'period', label: 'Ventas por Periodo', icon: TrendingUp },
    { id: 'performance', label: 'Rendimiento Sucursales', icon: Building2 },
    { id: 'cash', label: 'Corte de Caja', icon: DollarSign },
    { id: 'stock', label: 'Stock Bajo', icon: AlertTriangle },
    { id: 'top-products', label: 'Top Productos', icon: BarChart3 },
    { id: 'profit-margin', label: 'Margen', icon: Percent },
    { id: 'valuation', label: 'Valuacion', icon: Layers },
    { id: 'marketplace', label: 'Pedidos Online', icon: ShoppingBag },
    { id: 'quotes', label: 'Cotizaciones', icon: FileText },
  ];

  const needsRangeFilter = ['period', 'top-products', 'profit-margin', 'marketplace', 'quotes'].includes(activeTab);
  const needsBranchAndDate = ['daily', 'cash'].includes(activeTab);
  const needsBranchInRange = ['period', 'top-products', 'profit-margin'].includes(activeTab);

  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    preparing: 'Preparando',
    shipped: 'Enviado',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
    ready: 'Listo',
  };

  const quoteStatusLabels = {
    draft: 'Borrador',
    sent: 'Enviada',
    accepted: 'Aceptada',
    rejected: 'Rechazada',
    converted: 'Convertida',
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-indigo-100 text-indigo-700',
    shipped: 'bg-purple-100 text-purple-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    ready: 'bg-teal-100 text-teal-700',
  };

  const quoteStatusColors = {
    draft: 'bg-slate-100 text-slate-700',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    converted: 'bg-teal-100 text-teal-700',
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user?.business_id) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <BarChart3 className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay negocio configurado</h2>
          <p className="text-slate-500">Primero debes crear tu negocio para ver reportes.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Reportes</h1>
          <p className="text-slate-600 mt-2">Analiza el rendimiento de tu negocio</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto pb-px">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filters for branch + single date reports */}
        {needsBranchAndDate && branches.length > 0 && (
          <div className="flex flex-wrap gap-4 bg-white p-4 rounded-lg border border-slate-200">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Sucursal</label>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            {terminals.length > 0 && (
              <div className="min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">Caja</label>
                <select
                  value={selectedTerminal}
                  onChange={(e) => setSelectedTerminal(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todas</option>
                  {terminals.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        )}

        {/* Filters for date range reports */}
        {needsRangeFilter && (
          <div className="flex flex-wrap gap-4 bg-white p-4 rounded-lg border border-slate-200">
            {needsBranchInRange && branches.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">Sucursal</label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todas</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {needsBranchInRange && terminals.length > 0 && (
              <div className="min-w-[200px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">Caja</label>
                <select
                  value={selectedTerminal}
                  onChange={(e) => setSelectedTerminal(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todas</option>
                  {terminals.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Desde</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Hasta</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleApplyRangeFilter}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors"
              >
                Aplicar
              </button>
            </div>
          </div>
        )}

        {/* ==================== TAB: Daily Sales ==================== */}
        {activeTab === 'daily' && (
          <div className="space-y-6">
            {dailySales ? (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ventas Totales</p>
                      <p className="text-2xl font-bold text-slate-900">{formatCurrency(dailySales.total_sales)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Transacciones</p>
                      <p className="text-2xl font-bold text-slate-900">{dailySales.total_transactions}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Ticket Promedio</p>
                      <p className="text-2xl font-bold text-slate-900">{formatCurrency(dailySales.average_ticket)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <p className="text-sm text-slate-500 mb-3">Por Metodo de Pago</p>
                  {Object.keys(dailySales.payment_breakdown || {}).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(dailySales.payment_breakdown).map(([method, amount]) => (
                        <div key={method} className="flex justify-between text-sm">
                          <span className="text-slate-600 capitalize">{method === 'cash' ? 'Efectivo' : method === 'card' ? 'Tarjeta' : method}</span>
                          <span className="font-medium">{formatCurrency(amount)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">Sin datos</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Sin ventas registradas</h3>
                <p className="text-slate-500 mt-1">No hay ventas para la fecha seleccionada</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Sales by Period ==================== */}
        {activeTab === 'period' && (
          <div className="space-y-6">
            {salesByPeriod ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Total del Periodo</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(salesByPeriod.summary.total)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Transacciones</p>
                    <p className="text-2xl font-bold text-slate-900">{salesByPeriod.summary.transactions}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Ticket Promedio</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(salesByPeriod.summary.avg_ticket)}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {salesByPeriod.days.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Transacciones</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Ticket Promedio</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {salesByPeriod.days.map((day) => (
                          <tr key={day.date} className="hover:bg-slate-50">
                            <td className="px-6 py-4 text-sm text-slate-900">{day.date}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">{formatCurrency(day.total)}</td>
                            <td className="px-6 py-4 text-sm text-right text-slate-600">{day.transactions}</td>
                            <td className="px-6 py-4 text-sm text-right text-slate-600">{formatCurrency(day.average_ticket)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-12 text-center">
                      <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">Sin ventas en este periodo</h3>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Selecciona un rango de fechas</h3>
                <p className="text-slate-500 mt-1">Haz clic en Aplicar para ver los datos</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Branch Performance ==================== */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {branchPerformance.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {branchPerformance.map((branch) => (
                  <div key={branch.branch_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-200 bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-slate-500" />
                        <h3 className="font-semibold text-slate-900">{branch.branch_name}</h3>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-slate-500">Ventas Totales</p>
                          <p className="text-xl font-bold text-green-600">{formatCurrency(branch.total_sales)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Transacciones</p>
                          <p className="text-xl font-bold text-slate-900">{branch.total_transactions}</p>
                        </div>
                      </div>
                      {branch.top_products?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2">Top Productos</p>
                          <div className="space-y-2">
                            {branch.top_products.map((product, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="text-slate-600 truncate flex-1">{product.name}</span>
                                <span className="text-slate-500 mx-2">x{product.quantity}</span>
                                <span className="font-medium text-slate-900">{formatCurrency(product.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Sin datos de rendimiento</h3>
                <p className="text-slate-500 mt-1">Aun no hay ventas registradas</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Cash Close ==================== */}
        {activeTab === 'cash' && (
          <div className="space-y-6">
            {cashClose ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-lg font-semibold text-slate-900">Corte de Caja</h3>
                  <p className="text-sm text-slate-500">
                    {new Date(selectedDate).toLocaleDateString('es-MX', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Ventas en Efectivo</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(cashClose.cash_sales)}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Ventas con Tarjeta</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(cashClose.card_sales)}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Otros Metodos</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(cashClose.other_sales)}</span>
                      </div>
                    </div>
                    <div className="bg-teal-50 rounded-xl p-6">
                      <p className="text-sm text-teal-600 font-medium">Total del Dia</p>
                      <p className="text-4xl font-bold text-teal-700 mt-2">{formatCurrency(cashClose.total_sales)}</p>
                      <p className="text-sm text-teal-600 mt-2">{cashClose.total_transactions} transacciones</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Sin datos de corte</h3>
                <p className="text-slate-500 mt-1">No hay ventas para esta fecha</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Low Stock ==================== */}
        {activeTab === 'stock' && (
          <div className="space-y-4">
            {lowStock.length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={handleExportExcel}
                  disabled={exportingExcel}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  {exportingExcel ? 'Exportando...' : 'Exportar a Excel'}
                </button>
              </div>
            )}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {lowStock.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Producto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sucursal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stock Actual</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stock Minimo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lowStock.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.product_name}</td>
                        <td className="px-6 py-4 text-sm font-mono text-slate-600">{item.sku}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{item.branch_name}</td>
                        <td className="px-6 py-4 text-sm font-bold text-red-600">{item.current_stock}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{item.min_stock}</td>
                        <td className="px-6 py-4 text-sm">
                          {item.current_stock === 0 ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Agotado</span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Stock Bajo</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-12 text-center">
                  <Package className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-green-700">Todo en orden!</h3>
                  <p className="text-slate-500 mt-1">No hay productos con stock bajo</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== TAB: Top Products ==================== */}
        {activeTab === 'top-products' && (
          <div className="space-y-6">
            {topProducts.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase w-12">#</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Producto</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Unidades</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Ingreso</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase w-48"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topProducts.map((product, idx) => {
                      const maxRevenue = topProducts[0]?.revenue || 1;
                      const pct = (product.revenue / maxRevenue) * 100;
                      return (
                        <tr key={product.product_id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 text-sm font-bold text-slate-400">{idx + 1}</td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{product.name}</td>
                          <td className="px-6 py-4 text-sm text-right text-slate-600">{product.quantity}</td>
                          <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">{formatCurrency(product.revenue)}</td>
                          <td className="px-6 py-4">
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${pct}%` }}></div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Sin datos de productos</h3>
                <p className="text-slate-500 mt-1">No hay ventas en este periodo</p>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Profit Margin ==================== */}
        {activeTab === 'profit-margin' && (
          <div className="space-y-6">
            {profitMargin ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Ingresos Totales</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(profitMargin.summary.total_revenue)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Costo Total</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(profitMargin.summary.total_cost)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Margen Total</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(profitMargin.summary.total_margin)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">% Margen</p>
                    <p className="text-2xl font-bold text-teal-600">{profitMargin.summary.margin_pct}%</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {profitMargin.products.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Producto</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Ingreso</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Costo</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Margen</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">% Margen</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {profitMargin.products.map((p) => (
                          <tr key={p.product_id} className="hover:bg-slate-50">
                            <td className="px-6 py-4 text-sm font-medium text-slate-900">
                              {p.name}
                              {!p.has_cost && (
                                <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Sin dato de costo</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-right text-slate-900">{formatCurrency(p.revenue)}</td>
                            <td className="px-6 py-4 text-sm text-right text-red-600">{formatCurrency(p.cost_total)}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium text-green-600">{formatCurrency(p.margin)}</td>
                            <td className="px-6 py-4 text-sm text-right">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                p.margin_pct >= 50 ? 'bg-green-100 text-green-700' :
                                p.margin_pct >= 20 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {p.margin_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-12 text-center">
                      <Percent className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">Sin datos de margen</h3>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <Percent className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Cargando datos de margen...</h3>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Inventory Valuation ==================== */}
        {activeTab === 'valuation' && (
          <div className="space-y-6">
            {inventoryValuation ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Productos</p>
                    <p className="text-2xl font-bold text-slate-900">{inventoryValuation.grand_total.total_items}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Unidades Totales</p>
                    <p className="text-2xl font-bold text-slate-900">{inventoryValuation.grand_total.total_units}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Valor (Precio Venta)</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(inventoryValuation.grand_total.total_value_price)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Valor (Costo)</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(inventoryValuation.grand_total.total_value_cost)}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  {inventoryValuation.branches.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sucursal</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase"># Productos</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Unidades</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Valor (Venta)</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Valor (Costo)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {inventoryValuation.branches.map((b) => (
                          <tr key={b.branch_id} className="hover:bg-slate-50">
                            <td className="px-6 py-4 text-sm font-medium text-slate-900">{b.branch_name}</td>
                            <td className="px-6 py-4 text-sm text-right text-slate-600">{b.items_count}</td>
                            <td className="px-6 py-4 text-sm text-right text-slate-600">{b.total_units}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium text-green-600">{formatCurrency(b.total_value_price)}</td>
                            <td className="px-6 py-4 text-sm text-right font-medium text-blue-600">{formatCurrency(b.total_value_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50">
                        <tr>
                          <td className="px-6 py-3 text-sm font-bold text-slate-900">Total</td>
                          <td className="px-6 py-3 text-sm text-right font-bold text-slate-900">{inventoryValuation.grand_total.total_items}</td>
                          <td className="px-6 py-3 text-sm text-right font-bold text-slate-900">{inventoryValuation.grand_total.total_units}</td>
                          <td className="px-6 py-3 text-sm text-right font-bold text-green-600">{formatCurrency(inventoryValuation.grand_total.total_value_price)}</td>
                          <td className="px-6 py-3 text-sm text-right font-bold text-blue-600">{formatCurrency(inventoryValuation.grand_total.total_value_cost)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div className="p-12 text-center">
                      <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">Sin datos de inventario</h3>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Cargando valuacion...</h3>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Marketplace Orders ==================== */}
        {activeTab === 'marketplace' && (
          <div className="space-y-6">
            {marketplaceOrders ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Total Pedidos</p>
                    <p className="text-2xl font-bold text-slate-900">{marketplaceOrders.summary.total_orders}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Ingreso Total</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(marketplaceOrders.summary.total_revenue)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(marketplaceOrders.by_status).map(([status, data]) => (
                    <div key={status} className="bg-white rounded-xl p-5 border border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-slate-100 text-slate-700'}`}>
                          {statusLabels[status] || status}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900">{data.count}</p>
                      <p className="text-sm text-slate-500">{formatCurrency(data.total)}</p>
                    </div>
                  ))}
                </div>
                {Object.keys(marketplaceOrders.by_status).length === 0 && (
                  <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                    <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">Sin pedidos en este periodo</h3>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <ShoppingBag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Cargando pedidos...</h3>
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: Quotes Funnel ==================== */}
        {activeTab === 'quotes' && (
          <div className="space-y-6">
            {quotesFunnel ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Total Cotizaciones</p>
                    <p className="text-2xl font-bold text-slate-900">{quotesFunnel.total}</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Tasa de Conversion</p>
                    <p className="text-2xl font-bold text-teal-600">{quotesFunnel.conversion_rate}%</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <p className="text-sm text-slate-500">Valor Total Aceptadas</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(
                        quotesFunnel.funnel
                          .filter(f => f.status === 'accepted' || f.status === 'converted')
                          .reduce((sum, f) => sum + f.total, 0)
                      )}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="text-sm font-medium text-slate-700 mb-4">Embudo de Cotizaciones</h3>
                  <div className="space-y-3">
                    {quotesFunnel.funnel.map((stage) => {
                      const maxCount = Math.max(...quotesFunnel.funnel.map(f => f.count), 1);
                      const pct = (stage.count / maxCount) * 100;
                      return (
                        <div key={stage.status} className="flex items-center gap-4">
                          <div className="w-28 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${quoteStatusColors[stage.status] || 'bg-slate-100 text-slate-700'}`}>
                              {quoteStatusLabels[stage.status] || stage.status}
                            </span>
                          </div>
                          <div className="flex-1">
                            <div className="w-full bg-slate-100 rounded-full h-6 relative">
                              <div
                                className="bg-teal-500 h-6 rounded-full flex items-center justify-end pr-2 transition-all"
                                style={{ width: `${Math.max(pct, 5)}%` }}
                              >
                                <span className="text-xs text-white font-medium">{stage.count}</span>
                              </div>
                            </div>
                          </div>
                          <div className="w-28 text-right text-sm text-slate-600">
                            {formatCurrency(stage.total)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Cargando cotizaciones...</h3>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reportes;
